import { useCallback, useEffect, useMemo, useState } from 'react';
import { MISSION_NOTES_TAG } from '../../../../../../../../utils/docTags';
import { makeAttachedDocEntry } from '../../../../../../../../utils/attachedDocUtils';

/**
 * Attach / detach / create handlers and attach-picker UI state.
 */
export function useAttachedDocsActions({
  taskId,
  taskName,
  taskIcon,
  attachedDocs: attachedDocsRaw,
  libraryDocs,
  createDoc,
  onAttachedDocsChange,
  handleOpenDoc,
}) {
  const attachedDocs = useMemo(
    () => (Array.isArray(attachedDocsRaw) ? attachedDocsRaw : []),
    [attachedDocsRaw]
  );
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [attachQuery, setAttachQuery] = useState('');
  const [creatingDoc, setCreatingDoc] = useState(false);

  useEffect(() => {
    setAttachPickerOpen(false);
    setAttachQuery('');
  }, [taskId]);

  const handleAttachExisting = useCallback(
    (docId) => {
      if (!attachedDocs.some((e) => e.docId === docId)) {
        const doc = libraryDocs.find((d) => d.id === docId);
        const entry = makeAttachedDocEntry(doc) || { docId };
        onAttachedDocsChange?.(taskId, [...attachedDocs, entry]);
      }
      setAttachPickerOpen(false);
      setAttachQuery('');
    },
    [attachedDocs, libraryDocs, onAttachedDocsChange, taskId]
  );

  const handleDetach = useCallback(
    (docId) => {
      onAttachedDocsChange?.(
        taskId,
        attachedDocs.filter((e) => e.docId !== docId)
      );
    },
    [attachedDocs, onAttachedDocsChange, taskId]
  );

  const handleCreateAndAttach = useCallback(async () => {
    if (creatingDoc) return;
    setCreatingDoc(true);
    try {
      const doc = await createDoc({
        title: `${(taskName || '').trim() || 'Untitled Mission'} — Notes`,
        icon: taskIcon || '📄',
        tags: [MISSION_NOTES_TAG],
      });
      if (doc?.id) {
        onAttachedDocsChange?.(taskId, [
          ...attachedDocs,
          makeAttachedDocEntry(doc) || { docId: doc.id },
        ]);
        window.dispatchEvent(new CustomEvent('fokus:docs-changed'));
        handleOpenDoc(doc.id);
      }
    } catch (e) {
      console.error('Failed to create doc:', e);
    } finally {
      setCreatingDoc(false);
    }
  }, [
    creatingDoc,
    createDoc,
    taskName,
    taskIcon,
    taskId,
    attachedDocs,
    onAttachedDocsChange,
    handleOpenDoc,
  ]);

  return {
    attachPickerOpen,
    setAttachPickerOpen,
    attachQuery,
    setAttachQuery,
    creatingDoc,
    handleAttachExisting,
    handleDetach,
    handleCreateAndAttach,
  };
}
